import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { cn } from "@/lib/utils";

import { useLipChoice } from "../lip/lipStore";
import { useThreadId } from "../chat/useThreadId";
import type { AgentState } from "../store/agentSlice";
import { ApprovalCard } from "./ApprovalCard";
import { CARD_SCROLLER_PROPS, followBottom } from "./followCard";

const NONE: string[] = [];

/**
 * The strip's layout. One card fits: the strip takes its height and shrinks (scrolling) only when
 * the lip runs out of room, the thread below keeping its minimum (THREAD_VIEWPORT_CLASS). Two or
 * more are capped and scroll, so the thread stays readable.
 */
export function stripClass(count: number): string {
  return cn("min-h-0 overflow-y-auto px-4 pt-3 max-sm:px-3", count > 1 && "max-h-[60%]");
}

/**
 * Which card the strip brings to its buttons when cards appear (``followBottom``): the first one
 * when the strip fills (a thread opening with its waiting approvals, or a lone first one), the
 * newest when one arrives below others; none when fewer are shown, or while a link names a card
 * (the lip follows that one).
 */
export function followedCard(before: string[], now: string[], linked: string | null) {
  if (linked != null || now.length <= before.length) return null;
  return before.length === 0 ? now[0] : now[now.length - 1];
}

/**
 * The approvals of calls made outside this chat about its application (an app connected to Steddy
 * proposed them), above its messages: the same card as any approval, decided with the same
 * buttons, and its receipt once decided.
 */
export function ContextChoices() {
  const threadId = useThreadId();
  const refs = useSelector((state: { agent: AgentState }) =>
    threadId == null ? NONE : (state.agent.threads[threadId]?.contextChoices ?? NONE)
  );
  const linked = useLipChoice();
  const strip = useRef<HTMLDivElement>(null);
  const shown = useRef<string[]>(refs);
  // Outlives the effect: a snapshot that rebuilds the same list (a reconnect, a presence refresh)
  // re-runs it without stopping a follow under way. A new follow replaces it; unmounting ends it.
  const following = useRef<(() => void) | null>(null);
  useEffect(() => () => following.current?.(), []);

  useEffect(() => {
    const target = followedCard(shown.current, refs, linked);
    shown.current = refs;
    const card =
      target == null
        ? null
        : strip.current?.querySelector<HTMLElement>(`[data-choice-ref="${CSS.escape(target)}"]`);
    if (!card || !strip.current) return;
    following.current?.();
    following.current = followBottom(card, strip.current);
  }, [refs, linked]);

  if (!refs.length) return null;
  return (
    <div
      ref={strip}
      {...CARD_SCROLLER_PROPS}
      data-strip={refs.length > 1 ? "capped" : "fit"}
      className={stripClass(refs.length)}
    >
      <div className="mx-auto flex w-full max-w-thread flex-col gap-3">
        {refs.map((ref) => (
          <ApprovalCard key={ref} choiceRef={ref} />
        ))}
      </div>
    </div>
  );
}
